import {
  createFileRoute,
  Link,
  useSearch,
} from "@tanstack/react-router";
import { Schema } from "effect";

import {
  signInWithEmail,
  signInWithGitHub,
  signInWithGoogle,
} from "@/auth/auth-client";
import { OAuthAuthorizationSearchSchema } from "@/auth/oauth-types";
import { StructuredData } from "@/shared/components/structured-data";
import { useAppForm } from "@/shared/forms/form-context";
import { FormInput } from "@/shared/forms/form-input";
import { AuthLayout } from "@/shared/layout/auth-layout";
import { Button } from "@/shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/ui/card";
import {
  seo,
  createPageTitle,
  getCanonicalLinks,
} from "@/utils/seo";
import { Email, Password } from "@vivus/common";

const SignInSchema = Schema.Struct({
  email: Email,
  password: Password,
});

function SignInPage() {
  const search = useSearch({ from: "/sign-in" });

  // Check if this is an OAuth flow by looking for OAuth parameters
  const isOAuthFlow = Boolean(
    search.response_type ||
    search.client_id ||
    search.redirect_uri
  );

  const getCallbackURL = () =>
    isOAuthFlow
      ? `/oauth/consent${window.location.search}`
      : "/app/dashboard";

  const form = useAppForm({
    defaultValues: {
      email: "",
      password: "",
    },
    validators: {
      onChange: Schema.toStandardSchemaV1(SignInSchema),
      onSubmitAsync: async ({ value }) => {
        const callbackURL = getCallbackURL();

        try {
          const result = await signInWithEmail(
            value.email,
            value.password,
            callbackURL
          );

          if (result.error) {
            return (
              result.error ?? "Invalid email or password"
            );
          }
        } catch {
          return "An error occurred during sign in";
        }

        return undefined;
      },
    },
  });

  return (
    <AuthLayout>
      <StructuredData
        url="https://vivus.cz/sign-in"
        description="Sign in to your vivus account to access AI agents platform."
        about="User Authentication"
        titleParts={["Sign In"]}
      />
      <Card>
        <CardHeader className="text-center">
          <CardTitle variant="large">
            Welcome back
          </CardTitle>
          <CardDescription>
            Login with your GitHub or Google account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit();
            }}
          >
            <div className="grid gap-6">
              <div className="flex flex-col gap-4">
                <Button
                  variant="outline"
                  className="w-full"
                  type="button"
                  onClick={() =>
                    void signInWithGoogle(getCallbackURL())
                  }
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    className="mr-2 h-4 w-4"
                  >
                    <path
                      d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"
                      fill="currentColor"
                    />
                  </svg>
                  Login with Google
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  type="button"
                  onClick={() =>
                    void signInWithGitHub(getCallbackURL())
                  }
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    className="mr-2 h-4 w-4"
                  >
                    <path
                      d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12z"
                      fill="currentColor"
                    />
                  </svg>
                  Login with GitHub
                </Button>
              </div>
              <div className="relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t after:border-border">
                <span className="relative z-10 bg-card px-2 text-muted-foreground">
                  Or continue with
                </span>
              </div>
              <div className="grid gap-4">
                <FormInput
                  form={form}
                  name="email"
                  label="Email"
                  placeholder="m@example.com"
                  type="email"
                  required
                />
                <FormInput
                  form={form}
                  name="password"
                  label="Password"
                  type="password"
                  required
                />
                <form.Subscribe
                  selector={(state) => [
                    state.canSubmit,
                    state.isSubmitting,
                    state.errorMap.onSubmit,
                  ]}
                >
                  {([
                    canSubmit,
                    isSubmitting,
                    submitError,
                  ]) => (
                    <>
                      {submitError && (
                        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                          {submitError}
                        </div>
                      )}
                      <Button
                        type="submit"
                        className="w-full"
                        disabled={!canSubmit}
                      >
                        {isSubmitting
                          ? "Signing in..."
                          : "Sign In"}
                      </Button>
                    </>
                  )}
                </form.Subscribe>
              </div>
              <div className="mt-4 text-center text-sm">
                Don't have an account?{" "}
                <Link to="/sign-up" className="underline">
                  Sign up
                </Link>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
    </AuthLayout>
  );
}

export const Route = createFileRoute("/sign-in")({
  component: SignInPage,
  validateSearch: Schema.toStandardSchemaV1(
    OAuthAuthorizationSearchSchema
  ),
  head: () => ({
    meta: [
      ...seo({
        title: createPageTitle("Sign In"),
        description:
          "Sign in to your vivus account to access AI agents platform.",
        keywords:
          "vivus login, sign in, ai agents platform, secure authentication",
        canonical: "https://vivus.cz/sign-in",
      }),
    ],
    links: [
      ...getCanonicalLinks("https://vivus.cz/sign-in"),
    ],
  }),
});
